import {
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  PointElement,
  RadialLinearScale,
  Tooltip,
} from "chart.js";
import { Radar } from "react-chartjs-2";

ChartJS.register(RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

interface RadarChartProps {
  // candidateScores is keyed by `continuityId` so scores follow the dimension
  // across blueprint edits (the score's historical capability_dimension row id
  // diverges from the currently effective row id after an edit).
  dimensions: { id: string; continuityId: string; name: string; idealScore: number | null }[];
  candidateScores: Record<string, number>;
  candidateName: string;
  showBestFit?: boolean;
  size?: "sm" | "md" | "lg";
}

const SIZES = { sm: 240, md: 360, lg: 480 };
export const AXIS_MAX = 10;
export const SCORE_MAX = 5;
export const MEETS_SCORE = 3;

// Project a candidate's 1–5 evaluation onto the chart's 1–10 axis, using the
// dimension's required level as the inflection point. Score 3 ("meets the
// bar") plots exactly on the BEST FIT polygon; >3 lies outside, <3 inside.
// Without a required level, fall back to a linear 1..5 → 0..10 projection.
export function projectCandidateScore(
  score: number | undefined | null,
  requiredLevel: number | null,
): number {
  if (score == null) return 0;
  if (score <= 1) return 0;
  if (score >= SCORE_MAX) return AXIS_MAX;

  if (requiredLevel == null) {
    return ((score - 1) / (SCORE_MAX - 1)) * AXIS_MAX;
  }
  if (score <= MEETS_SCORE) {
    return (requiredLevel * (score - 1)) / (MEETS_SCORE - 1);
  }
  return (
    requiredLevel + ((AXIS_MAX - requiredLevel) * (score - MEETS_SCORE)) / (SCORE_MAX - MEETS_SCORE)
  );
}

function wrapLabel(text: string, maxChars: number): string[] {
  const words = text.split(" ");
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    if (current && `${current} ${word}`.length > maxChars) {
      lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) lines.push(current);
  return lines;
}

export function RadarChart({
  dimensions,
  candidateScores,
  candidateName,
  showBestFit = true,
  size = "md",
}: RadarChartProps) {
  const labels = dimensions.map((d) => wrapLabel(d.name, 20));
  const candidateData = dimensions.map((d) =>
    projectCandidateScore(candidateScores[d.continuityId], d.idealScore),
  );
  const bestFitData = dimensions.map((d) => d.idealScore ?? 0);
  const hasBestFit = showBestFit && dimensions.some((d) => d.idealScore != null);

  const datasets = [];

  if (hasBestFit) {
    datasets.push({
      label: "BEST FIT",
      data: bestFitData,
      borderColor: "#c2410c",
      backgroundColor: "rgba(194, 65, 12, 0.1)",
      borderWidth: 2,
      pointRadius: 2,
      pointBackgroundColor: "#c2410c",
    });
  }

  datasets.push({
    label: candidateName,
    data: candidateData,
    borderColor: "#1d4ed8",
    backgroundColor: "rgba(29, 78, 216, 0.15)",
    borderWidth: 2,
    pointRadius: 3,
    pointBackgroundColor: "#1d4ed8",
  });

  const px = SIZES[size];

  return (
    <div style={{ maxWidth: px + 120, margin: "0 auto" }}>
      <Radar
        data={{ labels, datasets }}
        options={{
          responsive: true,
          maintainAspectRatio: true,
          layout: { padding: size === "sm" ? 10 : 20 },
          scales: {
            r: {
              min: 0,
              max: AXIS_MAX,
              ticks: { stepSize: 2, font: { size: 10 }, backdropColor: "transparent" },
              pointLabels: {
                font: { size: size === "sm" ? 9 : 11 },
                padding: size === "sm" ? 8 : 14,
              },
              grid: { color: "rgba(0, 0, 0, 0.1)" },
              angleLines: { color: "rgba(0, 0, 0, 0.1)" },
            },
          },
          plugins: {
            legend: {
              position: "top",
              labels: { font: { size: 11 }, usePointStyle: true, pointStyle: "line" },
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (ctx) => {
                  const dim = dimensions[ctx.dataIndex];
                  if (ctx.dataset.label === "BEST FIT") {
                    return dim.idealScore != null
                      ? `Best fit: ${dim.idealScore}/${AXIS_MAX}`
                      : "Best fit: not set";
                  }
                  const raw = candidateScores[dim.continuityId];
                  if (raw == null) return `${ctx.dataset.label}: unscored`;
                  const required =
                    dim.idealScore != null ? ` (required ${dim.idealScore}/${AXIS_MAX})` : "";
                  return `${ctx.dataset.label}: ${raw}/${SCORE_MAX}${required}`;
                },
              },
            },
          },
        }}
      />
    </div>
  );
}
